import { DateRangeSchema } from '@multiplier/lib-shared-date';
import { convertToStandardErrorAndThrow, ValidationError } from '@multiplier/lib-shared-errors';
import { Currency, MonetaryLedgerBalanceBagSchema, MonetaryLedgerBalanceSchema, MonetaryMetadataSchema, } from '@multiplier/lib-shared-monetary';
import { z } from 'zod';
// Row provenance schema
export const RowProvenanceSchema = z.enum(['xero_account', 'calculation', 'self', 'uploaded_file']);
// Ledger polarity schema
export const LedgerPolaritySchema = z.enum(['creditPositive', 'debitPositive']);
// =============================================================================
// Schema factory: produces ReportRow, RowGroup, and Report schemas for any value schema
// =============================================================================
/**
 * Creates mutually-recursive ReportRow/RowGroup/Report schemas parameterized by value schema.
 *
 * Uses the same z.lazy()/getter trick as the original non-generic schemas
 * to handle the RowGroup -> ReportRow -> RowGroup recursion.
 */
function createReportSchemas(valueSchema) {
    // ReportRow is defined first; its children getter creates a closure over rowGroupSchema
    // which is assigned below (safe because getters are only evaluated at access time).
    const reportRowSchema = z.object({
        name: z.string().describe('Report row name'),
        value: valueSchema.describe('Value of the report row'),
        get children() {
            return rowGroupSchema.optional();
        },
        id: z.string().brand(),
        provenance: RowProvenanceSchema,
        metadata: MonetaryMetadataSchema.optional(),
    });
    // z.lazy defers evaluation of the inner schema, breaking the circular reference.
    // The z.ZodType annotation provides the forward-declared output type for mutual recursion.
    const rowGroupSchema = z.lazy(() => z.object({
        ledgerPolarity: LedgerPolaritySchema.describe('Ledger polarity for this group'),
        rows: z.array(reportRowSchema).describe('Report rows in this group'),
    }));
    const reportSchema = z.object({
        dateRange: DateRangeSchema.describe('Date range of the Free Cash Flow report'),
        generatedAt: z.iso.datetime().describe('Date and time the report was generated'),
        rowGroup: rowGroupSchema.describe('Report rows in RowGroup format'),
        entity: z.string().brand(),
        reportId: z.string().brand(),
        reportName: z.string().brand(),
    });
    return { reportRowSchema, rowGroupSchema, reportSchema };
}
// =============================================================================
// MonetaryLedgerBalance instantiation (preserves existing exports)
// =============================================================================
const mlbSchemas = createReportSchemas(MonetaryLedgerBalanceSchema);
export const ReportRowSchema = mlbSchemas.reportRowSchema;
export const RowGroupSchema = mlbSchemas.rowGroupSchema;
export const ReportSchema = mlbSchemas.reportSchema;
// =============================================================================
// MoneyBag<LedgerBalance> instantiation (new exports)
// =============================================================================
const moneyBagSchemas = createReportSchemas(MonetaryLedgerBalanceBagSchema);
export const MoneyBagReportRowSchema = moneyBagSchemas.reportRowSchema;
export const MoneyBagRowGroupSchema = moneyBagSchemas.rowGroupSchema;
export const MoneyBagReportSchema = moneyBagSchemas.reportSchema;
// Helper schemas and functions to convert strings to branded types with validation
/** Branded schema for RowID - exported for use in API schemas */
export const RowIDSchema = z.string().brand();
const EntityNameSchema = z.string().brand();
/** Branded schema for ReportName - exported for use in UI parsing */
export const ReportNameSchema = z.string().min(1, 'Report name is required').brand();
/** Branded schema for ReportID - exported for use in API schemas and UI parsing */
export const ReportIDSchema = z.string().min(1, 'Report ID is required').brand();
/** Branded schema for BusinessContext - exported for use in API schemas and storage */
export const BusinessContextSchema = z.string().min(1, 'Business context is required').brand();
export function toRowId(value) {
    try {
        return RowIDSchema.parse(value);
    }
    catch (error) {
        convertToStandardErrorAndThrow(error, (err) => new ValidationError(`Invalid row ID: ${value}`, undefined, err));
    }
}
export function toEntityName(value) {
    try {
        return EntityNameSchema.parse(value);
    }
    catch (error) {
        convertToStandardErrorAndThrow(error, (err) => new ValidationError(`Invalid entity name: ${value}`, undefined, err));
    }
}
export function toReportId(value) {
    try {
        return ReportIDSchema.parse(value);
    }
    catch (error) {
        convertToStandardErrorAndThrow(error, (err) => new ValidationError(`Invalid report ID: ${value}`, undefined, err));
    }
}
export function toReportName(value) {
    try {
        return ReportNameSchema.parse(value);
    }
    catch (error) {
        convertToStandardErrorAndThrow(error, (err) => new ValidationError(`Invalid report name: ${value}`, undefined, err));
    }
}
/**
 * Extracts all currency codes from a Report by traversing the RowGroup tree.
 * Used by export functionality to validate single-currency reports.
 *
 * @param report - The report to extract currencies from
 * @returns Set of currency codes found in the report (excludes null currencies from zero values)
 * @throws ValidationError if any currency code is invalid
 */
export function getCurrenciesFromReport(report) {
    const currencies = new Set();
    function processRowGroup(rowGroup) {
        for (const row of rowGroup.rows) {
            // Add currency if not null (zero values can have null currency)
            if (row.value.currency !== null) {
                const currency = Currency.fromCode(row.value.currency);
                currencies.add(currency.code);
            }
            // Recursively process children if they exist
            if (row.children !== undefined) {
                processRowGroup(row.children);
            }
        }
    }
    processRowGroup(report.rowGroup);
    return currencies;
}
//# sourceMappingURL=BusinessAnalysisSchemas.js.map